/**
 * Vessel Readiness Score — a 0-100 single-number summary of how
 * inspection-ready a vessel is, computed entirely from data we
 * already pull on `VesselProfile`. No new endpoints, no schema.
 *
 * The score is OBSERVATION-ONLY. It never tells the operator what to
 * do — it surfaces the signals an inspector / chartering team would
 * already weigh: PSC detention history, deficiency volume, class
 * status, age. The breakdown is exposed alongside the headline so
 * the user can audit how it was reached.
 *
 * Methodology (kept transparent on purpose):
 *
 *   Start at 100, subtract penalties:
 *
 *     PSC signals (last 24 months window in `recent_inspections`)
 *       - any detention in window:                    -25
 *       - more than 1 detention:                      additional -10 per
 *       - per deficiency over a 3-per-inspection avg: -2 each, capped -15
 *       - any open / not-rectified deficiency:        -5
 *
 *     Class status
 *       - status === 'suspended' / 'withdrawn':       -30
 *       - status === 'expired'   / class_expiry past: -20
 *       - status === 'in class' (default green):       0
 *
 *     Vessel age (year_built)
 *       - age >= 25:                                  -10
 *       - age >= 20:                                  -5
 *
 *     Sanctions
 *       - any active sanction (no removal_date):      -40 (hard signal)
 *
 *   Result is clamped to [0, 100] and bucketed:
 *     85+   → "Ready"        (emerald)
 *     65-84 → "Watch"        (cyan)
 *     45-64 → "Concerns"     (amber)
 *     <45   → "High risk"    (red)
 *
 * This is not a charter decision. It's a triage signal — a cheap way
 * for the inspector to decide which vessels in their fleet need
 * deeper attention this week.
 *
 * NOTE: since M2 of the operator platform, the CANONICAL computation lives
 * server-side in workers/src/lib/readiness.ts (persisted on risk_scores,
 * exposed via GET /api/v2/vessels/:id/readiness) and includes factors this
 * local model can't see (open corrective actions, open PSC deficiencies).
 * `computeReadiness` here is the offline/missing-data FALLBACK only —
 * prefer `resultFromServer` with the API payload.
 */
import type { VesselProfile } from '../../types/vessel';

export type ReadinessBand = 'ready' | 'watch' | 'concerns' | 'high_risk';

export interface ReadinessFactor {
  /** Stable id for testing / debugging. */
  id: string;
  /** Human label shown next to the score. */
  label: string;
  /** Negative penalty applied to the 100 baseline. */
  penalty: number;
  /** Whether this signal is "active" (penalty != 0). */
  active: boolean;
}

export interface ReadinessResult {
  /** Final clamped score, 0-100. */
  score: number;
  /** Categorical band derived from `score`. */
  band: ReadinessBand;
  /** Headline label for the band. */
  bandLabel: string;
  /** All factors that contributed (active + inactive). */
  factors: ReadinessFactor[];
  /** Generation timestamp — useful for "as of" footers. */
  computedAt: string;
}

const TWO_YEARS_MS = 2 * 365 * 24 * 3600_000;

export function computeReadiness(v: VesselProfile): ReadinessResult {
  const now = Date.now();
  const factors: ReadinessFactor[] = [];

  // ── PSC signals ──────────────────────────────────────────────
  const recent = (v.recent_inspections ?? []).filter((i) => {
    if (!i.inspection_date) return false;
    return now - +new Date(i.inspection_date) <= TWO_YEARS_MS;
  });

  const detentions = recent.filter((i) => i.detained).length;
  const detentionPenalty =
    detentions === 0 ? 0
  : detentions === 1 ? 25
                     : 25 + (detentions - 1) * 10;
  factors.push({
    id: 'psc-detentions',
    label: detentions === 0 ? 'No PSC detentions in 24 months' : `${detentions} PSC detention${detentions > 1 ? 's' : ''} in 24 months`,
    penalty: -detentionPenalty,
    active: detentionPenalty > 0,
  });

  const totalDefs = recent.reduce((s, i) => s + (i.deficiency_count ?? 0), 0);
  const avgPerInspection = recent.length > 0 ? totalDefs / recent.length : 0;
  const defOverage = Math.max(0, avgPerInspection - 3);
  const defPenalty = Math.min(15, Math.round(defOverage * 2));
  factors.push({
    id: 'psc-deficiency-density',
    label:
      recent.length === 0
        ? 'No recent PSC inspections on file'
        : `Avg ${avgPerInspection.toFixed(1)} deficiencies per inspection (${totalDefs} total / ${recent.length} inspection${recent.length > 1 ? 's' : ''})`,
    penalty: -defPenalty,
    active: defPenalty > 0,
  });

  const hasOpen = recent.some((i) =>
    (i.deficiencies ?? []).some((d) => d.rectified === false),
  );
  factors.push({
    id: 'psc-open-deficiency',
    label: hasOpen ? 'At least one open PSC deficiency' : 'No open PSC deficiencies',
    penalty: hasOpen ? -5 : 0,
    active: hasOpen,
  });

  // ── Class status ─────────────────────────────────────────────
  const classStatus = (v.class_status ?? '').toLowerCase();
  const classExpiry = v.class_expiry ? +new Date(v.class_expiry) : null;
  const expired =
    classStatus.includes('expired')   ||
    classStatus.includes('withdrawn') ||
    classStatus.includes('suspended') ||
    (classExpiry != null && classExpiry < now);
  const suspended = classStatus.includes('suspended') || classStatus.includes('withdrawn');
  const classPenalty = suspended ? 30 : expired ? 20 : 0;
  factors.push({
    id: 'class-status',
    label:
      suspended ? 'Class suspended / withdrawn'
    : expired   ? 'Class expired or past renewal date'
                : v.class_status ? `Class: ${v.class_status}` : 'Class status not recorded',
    penalty: -classPenalty,
    active: classPenalty > 0,
  });

  // ── Age ──────────────────────────────────────────────────────
  let agePenalty = 0;
  let ageLabel = 'Vessel age not recorded';
  if (v.year_built) {
    const age = new Date().getFullYear() - Number(v.year_built);
    ageLabel = `Vessel built ${v.year_built} (${age} years old)`;
    if (age >= 25)      agePenalty = 10;
    else if (age >= 20) agePenalty = 5;
  }
  factors.push({
    id: 'age',
    label: ageLabel,
    penalty: -agePenalty,
    active: agePenalty > 0,
  });

  // ── Sanctions ────────────────────────────────────────────────
  const activeSanctions = (v.sanctions ?? []).filter((s) => !s.removal_date);
  const sancPenalty = activeSanctions.length > 0 ? 40 : 0;
  factors.push({
    id: 'sanctions',
    label:
      activeSanctions.length === 0
        ? 'No active sanctions'
        : `${activeSanctions.length} active sanction${activeSanctions.length > 1 ? 's' : ''} on file`,
    penalty: -sancPenalty,
    active: sancPenalty > 0,
  });

  const total = factors.reduce((s, f) => s + f.penalty, 0);
  const score = Math.max(0, Math.min(100, 100 + total));
  const band = bandFor(score);

  return {
    score,
    band,
    bandLabel: BAND_LABEL[band],
    factors,
    computedAt: new Date().toISOString(),
  };
}

export const BAND_LABEL: Record<ReadinessBand, string> = {
  ready:     'Ready',
  watch:     'Watch',
  concerns:  'Concerns',
  high_risk: 'High risk',
};

/**
 * Shape of the canonical server-side readiness payload
 * (GET /api/v2/vessels/:id/readiness — computed by workers/src/lib/readiness.ts).
 */
export interface ServerReadiness {
  vessel_id: string;
  readiness_score: number;
  readiness_band: ReadinessBand;
  factors: ReadinessFactor[];
  computed_at: string;
}

/** Adapt the server payload into the shape the badge components render. */
export function resultFromServer(server: ServerReadiness): ReadinessResult {
  return {
    score: server.readiness_score,
    band: server.readiness_band,
    bandLabel: BAND_LABEL[server.readiness_band],
    factors: server.factors ?? [],
    computedAt: server.computed_at,
  };
}

function bandFor(score: number): ReadinessBand {
  if (score >= 85) return 'ready';
  if (score >= 65) return 'watch';
  if (score >= 45) return 'concerns';
  return 'high_risk';
}

/** Tailwind utility groups per band — used by the badge component. */
export const BAND_CLS: Record<ReadinessBand, { bg: string; ring: string; text: string; dot: string }> = {
  ready:     { bg: 'bg-emerald-500/10', ring: 'border-emerald-500/40', text: 'text-emerald-200', dot: 'bg-emerald-400' },
  watch:     { bg: 'bg-cyan-500/10',    ring: 'border-cyan-500/40',    text: 'text-cyan-200',    dot: 'bg-cyan-400' },
  concerns:  { bg: 'bg-amber-500/10',   ring: 'border-amber-500/40',   text: 'text-amber-200',   dot: 'bg-amber-400' },
  high_risk: { bg: 'bg-red-500/10',     ring: 'border-red-500/40',     text: 'text-red-200',     dot: 'bg-red-400' },
};
